import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  fromRFC3339,
  getPresetById,
  type TimeRange,
  type TimeRangePresetId,
  isNow,
  isRelativeTime,
  isRFC3339Date,
  parseRelativeTime,
} from "../utils/time";

export function useServiceInstanceParams(): {
  serviceSlug: string | null;
  svcName: string | null;
  svcInstance: string | null;
} {
  const params = useParams();
  const serviceSlug = params.service ?? null;
  const svcName = params.svcName ?? null;
  const svcInstance = params.svcInstance ?? null;

  return { serviceSlug, svcName, svcInstance };
}

export interface UseTimeRangeOptions {
  defaultPresetId?: TimeRangePresetId;
  defaultFrom?: string; // e.g., "30m", "2022-07-08T00:14:07Z", or "now"
  defaultStop?: string; // e.g., "now", "2022-07-08T00:14:07Z", or "5m"
}

export interface UseTimeRangeResult {
  range: TimeRange;
  activePresetId: TimeRangePresetId | null;
  setPreset: (id: TimeRangePresetId) => void;
  setRange: (range: TimeRange) => void;
  setCustomRange: (from: string | null, stop: string | null) => void;
  rangeFromStr: string | null;
  rangeStopStr: string | null;
}

function parseCsvValues(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function useCsvValuesFromQuery(paramName: string): string[] {
  const [searchParams] = useSearchParams();
  const raw = searchParams.get(paramName);
  return useMemo(() => parseCsvValues(raw), [raw]);
}

export function useTimeRangeFromQuery(_options: UseTimeRangeOptions = {}): UseTimeRangeResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const range = useMemo<TimeRange>(() => {
    const fromStr = searchParams.get("range_from");
    const toStr = searchParams.get("range_stop");

    let computedFrom: Date | null = null;
    let computedTo: Date | null = null;

    // Parse 'to' first as it may be needed to compute 'from'
    if (toStr) {
      if (isNow(toStr)) {
        computedTo = new Date();
        computedTo.setSeconds(0, 0);
      } else if (isRFC3339Date(toStr)) {
        computedTo = fromRFC3339(toStr);
        if (computedTo) {
          computedTo.setSeconds(0, 0);
        }
      } else if (isRelativeTime(toStr)) {
        // Relative time for 'to' - will be computed based on 'from' if available
        const relativeMs = parseRelativeTime(toStr);
        if (relativeMs !== null) {
          // We'll compute this after we know 'from'
        }
      }
    }

    // Parse 'from'
    if (fromStr) {
      if (isRFC3339Date(fromStr)) {
        computedFrom = fromRFC3339(fromStr);
        if (computedFrom) {
          computedFrom.setSeconds(0, 0);
        }
        // If 'to' is relative, compute it relative to 'from'
        if (toStr && isRelativeTime(toStr)) {
          const relativeMs = parseRelativeTime(toStr);
          if (relativeMs !== null && computedFrom) {
            computedTo = new Date(computedFrom.getTime() + relativeMs);
            computedTo.setSeconds(0, 0);
          }
        }
      } else if (isRelativeTime(fromStr)) {
        // If 'from' is relative, compute it relative to 'to'
        const relativeMs = parseRelativeTime(fromStr);
        if (relativeMs !== null) {
          if (computedTo) {
            computedFrom = new Date(computedTo.getTime() - relativeMs);
            computedFrom.setSeconds(0, 0);
          } else if (isNow(toStr) || !toStr) {
            // Default to 'now' if 'to' is not specified
            computedTo = new Date();
            computedTo.setSeconds(0, 0);
            computedFrom = new Date(computedTo.getTime() - relativeMs);
            computedFrom.setSeconds(0, 0);
          }
        }
      }
    }

    // If we have both computed dates, return them
    if (computedFrom && computedTo) {
      return {
        from: new Date(computedFrom.getTime()),
        to: new Date(computedTo.getTime()),
      };
    }

    // Fall back to default: stop = now, from = 5m
    const now = new Date();
    now.setSeconds(0, 0);
    const from5m = new Date(now.getTime() - 5 * 60 * 1000);
    from5m.setSeconds(0, 0);
    return {
      from: new Date(from5m.getTime()),
      to: new Date(now.getTime()),
    };
  }, [searchParams]);

  const activePresetId: TimeRangePresetId | null = useMemo(() => {
    const presetId = searchParams.get("range_preset") as TimeRangePresetId | null;
    if (presetId && getPresetById(presetId)) {
      return presetId;
    }
    return null;
  }, [searchParams]);

  const patchSearchParams = (updater: (draft: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        updater(next);
        return next;
      },
      { replace: false },
    );
  };

  const setPreset = (id: TimeRangePresetId) => {
    const preset = getPresetById(id);
    if (!preset) return;
    const { from, to } = preset.compute();
    patchSearchParams((draft) => {
      draft.set("range_from", from.toISOString());
      draft.set("range_stop", to.toISOString());
      draft.set("range_preset", preset.id);
    });
  };

  const setRange = (value: TimeRange) => {
    patchSearchParams((draft) => {
      draft.set("range_from", value.from.toISOString());
      draft.set("range_stop", value.to.toISOString());
      draft.delete("range_preset");
    });
  };

  const setCustomRange = (from: string | null, stop: string | null) => {
    patchSearchParams((draft) => {
      // Don't write range_from if it's "5m" (default value)
      if (from && from !== "5m") {
        draft.set("range_from", from);
      } else {
        draft.delete("range_from");
      }
      // Don't write range_stop if it's "now" (default value)
      if (stop && !isNow(stop)) {
        draft.set("range_stop", stop);
      } else {
        draft.delete("range_stop");
      }
      draft.delete("range_preset");
    });
  };

  return {
    range,
    activePresetId,
    setPreset,
    setRange,
    setCustomRange,
    rangeFromStr: searchParams.get("range_from"),
    rangeStopStr: searchParams.get("range_stop"),
  };
}

export interface UseTraceFilterResult {
  traceIds: string[];
  setTraceIds: (ids: string[]) => void;
}

export function useTraceFilterFromQuery(): UseTraceFilterResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get("trace_ids") || searchParams.get("filter_traces");
  const traceIds = useMemo(() => parseCsvValues(raw), [raw]);

  const setTraceIds = (ids: string[]) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (ids.length === 0) {
          next.delete("trace_ids");
          next.delete("filter_traces");
        } else {
          next.set("trace_ids", ids.join(","));
          next.delete("filter_traces");
        }
        return next;
      },
      { replace: true },
    );
  };

  return { traceIds, setTraceIds };
}

export interface UseServiceNamesFilterResult {
  serviceNames: string[];
}

export function useServiceNamesFilterFromQuery(): UseServiceNamesFilterResult {
  return { serviceNames: useCsvValuesFromQuery("service_names") };
}

export interface UseServiceInstancesFilterResult {
  serviceInstances: string[];
}

export function useServiceInstancesFilterFromQuery(): UseServiceInstancesFilterResult {
  return { serviceInstances: useCsvValuesFromQuery("service_instances") };
}

export interface UseMetricNamesFilterResult {
  metricNames: string[];
}

export function useMetricNamesFilterFromQuery(): UseMetricNamesFilterResult {
  return { metricNames: useCsvValuesFromQuery("metric_names") };
}

export interface UseSpanIdsFilterResult {
  spanIds: string[];
}

export function useSpanIdsFilterFromQuery(): UseSpanIdsFilterResult {
  return { spanIds: useCsvValuesFromQuery("span_ids") };
}

export type IsRootFilterValue = "true" | "false" | undefined;

export interface UseIsRootFilterResult {
  isRoot: IsRootFilterValue;
  setIsRoot: (value: IsRootFilterValue) => void;
}

export function useIsRootFilterFromQuery(): UseIsRootFilterResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const isRoot = useMemo<IsRootFilterValue>(() => {
    const raw = searchParams.get("is_root");
    if (raw === "true") return "true";
    if (raw === "false") return "false";
    return undefined;
  }, [searchParams]);

  const setIsRoot = (value: IsRootFilterValue) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value == null || value === undefined) {
          next.delete("is_root");
        } else {
          next.set("is_root", value);
        }
        return next;
      },
      { replace: true },
    );
  };

  return { isRoot, setIsRoot };
}

export interface UseDurationFilterOptions {
  minParam?: string;
  maxParam?: string;
}

export interface UseDurationFilterResult {
  minDurationMs: number | null;
  maxDurationMs: number | null;
  setMinDurationMs: (value: number | null) => void;
  setMaxDurationMs: (value: number | null) => void;
}

export function useDurationFilterFromQuery(
  options: UseDurationFilterOptions = {},
): UseDurationFilterResult {
  const { minParam = "min_duration_ms", maxParam = "max_duration_ms" } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const minDurationMs = useMemo(() => {
    const raw = searchParams.get(minParam);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) || n < 0 ? null : n;
  }, [searchParams, minParam]);

  const maxDurationMs = useMemo(() => {
    const raw = searchParams.get(maxParam);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) || n < 0 ? null : n;
  }, [searchParams, maxParam]);

  const setMinDurationMs = (value: number | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value == null) {
          next.delete(minParam);
        } else {
          next.set(minParam, String(Math.round(value)));
        }
        return next;
      },
      { replace: true },
    );
  };

  const setMaxDurationMs = (value: number | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value == null) {
          next.delete(maxParam);
        } else {
          next.set(maxParam, String(Math.round(value)));
        }
        return next;
      },
      { replace: true },
    );
  };

  return { minDurationMs, maxDurationMs, setMinDurationMs, setMaxDurationMs };
}

export type SeverityRange = "TRACE" | "DEBUG" | "INFO" | "WARN" | "ERROR" | "FATAL";

const ALL_SEVERITY_RANGES: readonly SeverityRange[] = [
  "TRACE",
  "DEBUG",
  "INFO",
  "WARN",
  "ERROR",
  "FATAL",
] as const;

export interface UseSeverityRangesFilterResult {
  severityRanges: SeverityRange[];
  setSeverityRanges: (ranges: SeverityRange[]) => void;
}

export function useSeverityRangesFilterFromQuery(): UseSeverityRangesFilterResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const severityRanges = useMemo<SeverityRange[]>(() => {
    const raw = searchParams.get("severity_ranges");
    if (!raw) return [];

    const parsed = raw
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s.length > 0);

    const set = new Set<SeverityRange>();
    for (const v of parsed) {
      if (ALL_SEVERITY_RANGES.includes(v as SeverityRange)) {
        set.add(v as SeverityRange);
      }
    }

    return Array.from(set);
  }, [searchParams]);

  const setSeverityRanges = (ranges: SeverityRange[]) => {
    const normalized = Array.from(
      new Set(ranges.map((r) => String(r).trim().toUpperCase()).filter((r) => r.length > 0)),
    ).filter((r) => ALL_SEVERITY_RANGES.includes(r as SeverityRange)) as SeverityRange[];

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        // Treat "none" and "all" as "no filter" (remove param).
        if (normalized.length === 0 || normalized.length === ALL_SEVERITY_RANGES.length) {
          next.delete("severity_ranges");
        } else {
          next.set("severity_ranges", normalized.join(","));
        }

        return next;
      },
      { replace: true },
    );
  };

  return { severityRanges, setSeverityRanges };
}

export type RefreshMode = "off" | "2s" | "30s" | "1m" | "5m";

const ALL_REFRESH_MODES: readonly RefreshMode[] = ["off", "2s", "30s", "1m", "5m"] as const;

export interface UseRefreshModeResult {
  refreshMode: RefreshMode;
  setRefreshMode: (mode: RefreshMode) => void;
}

/**
 * Query param: `refresh`
 * - omitted or "off" => Off (default)
 * - "30s" | "1m" | "5m" => enable auto refresh
 */
export function useRefreshModeFromQuery(): UseRefreshModeResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const refreshMode = useMemo<RefreshMode>(() => {
    const raw = searchParams.get("refresh");
    if (!raw) return "off";
    const normalized = raw.trim().toLowerCase();
    if (normalized === "2s" || normalized === "30s" || normalized === "1m" || normalized === "5m") {
      return normalized as RefreshMode;
    }
    return "off";
  }, [searchParams]);

  const setRefreshMode = (mode: RefreshMode) => {
    const normalized = ALL_REFRESH_MODES.includes(mode) ? mode : "off";
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (normalized === "off") {
          next.delete("refresh");
        } else {
          next.set("refresh", normalized);
        }
        return next;
      },
      { replace: true },
    );
  };

  return { refreshMode, setRefreshMode };
}
